/**
 * useFileSizeLimit Hook
 *
 * Provides dynamic file size limits based on user's subscription plan.
 * Falls back to FREE plan limit (10MB) if subscription data is not available.
 */

import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth/contexts/AuthContext';

/** Default file size limit in MB (FREE plan - matches database) */
const DEFAULT_MAX_FILE_SIZE_MB = 10;

export interface FileSizeLimitResult {
  /** Maximum file size in megabytes */
  maxFileSizeMB: number;
  /** Maximum file size in bytes */
  maxFileSizeBytes: number;
  /** Whether subscription data is still loading */
  isLoading: boolean;
  /** Validate if a file size (in bytes) is within the limit */
  isFileSizeValid: (fileSizeBytes: number) => boolean;
  /** Format the max file size for display (e.g., "100 MB") */
  formattedMaxSize: string;
}

/**
 * Hook to get dynamic file size limits based on subscription plan
 *
 * @returns FileSizeLimitResult with limit values and validation helper
 *
 * @example
 * const { maxFileSizeBytes, isFileSizeValid } = useFileSizeLimit();
 *
 * if (!isFileSizeValid(file.size)) {
 *   setError(t('errors.fileTooLarge', { maxSize: formattedMaxSize }));
 * }
 */
export const useFileSizeLimit = (): FileSizeLimitResult => {
  const { subscription } = useAuth();
  const { t } = useTranslation();
  const maxFileSizeMB = subscription?.max_file_size_mb ?? DEFAULT_MAX_FILE_SIZE_MB;
  const isUnlimited = maxFileSizeMB < 0;
  const maxFileSizeBytes = isUnlimited ? Infinity : maxFileSizeMB * 1024 * 1024;

  const isFileSizeValid = (fileSizeBytes: number): boolean => {
    return isUnlimited || fileSizeBytes <= maxFileSizeBytes;
  };

  const formattedMaxSize = isUnlimited
    ? t('pricing.feature.fileSizeUnlimited', 'Unlimited')
    : maxFileSizeMB >= 1024
      ? `${(maxFileSizeMB / 1024).toFixed(1)} GB`
      : `${maxFileSizeMB} MB`;

  return {
    maxFileSizeMB,
    maxFileSizeBytes,
    isLoading: !subscription,
    isFileSizeValid,
    formattedMaxSize,
  };
};

/**
 * Utility function for non-hook contexts (e.g., validation utilities)
 * Use this when you can't use hooks but have access to subscription data
 *
 * @param maxFileSizeMB - Max file size in MB from subscription plan
 * @returns Object with validation helpers
 */
export const createFileSizeValidator = (maxFileSizeMB: number = DEFAULT_MAX_FILE_SIZE_MB) => {
  const isUnlimited = maxFileSizeMB < 0;
  const maxFileSizeBytes = isUnlimited ? Infinity : maxFileSizeMB * 1024 * 1024;

  return {
    maxFileSizeMB,
    maxFileSizeBytes,
    isFileSizeValid: (fileSizeBytes: number): boolean =>
      isUnlimited || fileSizeBytes <= maxFileSizeBytes,
    formattedMaxSize: isUnlimited
      ? 'Unlimited'
      : maxFileSizeMB >= 1024
        ? `${(maxFileSizeMB / 1024).toFixed(1)} GB`
        : `${maxFileSizeMB} MB`,
  };
};

export { DEFAULT_MAX_FILE_SIZE_MB };
